/**
 * Base Tool Provider
 *
 * Abstract base class that provides common functionality for tool providers.
 * Extend this class to create new providers (LangChain, Google ADK, etc.)
 */

import type {
  Tool,
  ToolProvider,
  ToolProviderType,
} from '../types'

export abstract class BaseToolProvider implements ToolProvider {
  abstract readonly name: ToolProviderType
  abstract readonly displayName: string
  abstract readonly description: string

  protected tools: Tool[] = []
  protected initialized = false

  async initialize(): Promise<void> {
    if (this.initialized) {
      return
    }

    await this.loadTools()
    this.initialized = true
  }

  /**
   * Load tools from the provider's source
   * Override this method in subclasses
   */
  protected abstract loadTools(): Promise<void>

  async listTools(): Promise<Tool[]> {
    await this.ensureInitialized()
    return [...this.tools]
  }

  async getToolById(id: string): Promise<Tool | null> {
    await this.ensureInitialized()
    return this.tools.find((t) => t.id === id) ?? null
  }

  async searchTools(query: string): Promise<Tool[]> {
    await this.ensureInitialized()
    const lowerQuery = query.toLowerCase()
    return this.tools.filter(
      (t) =>
        t.name.toLowerCase().includes(lowerQuery) ||
        t.description.toLowerCase().includes(lowerQuery)
    )
  }

  async getToolsByCategory(category: string): Promise<Tool[]> {
    await this.ensureInitialized()
    return this.tools.filter((t) => t.category === category)
  }

  async validateConfig(toolId: string, config: unknown): Promise<boolean> {
    const tool = await this.getToolById(toolId)
    if (!tool) {
      return false
    }

    // If no config schema, accept any config
    if (!tool.configSchema) {
      return true
    }

    // Basic validation - can be extended
    if (typeof config !== 'object' || config === null) {
      return false
    }

    return true
  }

  async getCategories(): Promise<string[]> {
    await this.ensureInitialized()
    const categories = new Set(this.tools.map((t) => t.category))
    return Array.from(categories)
  }

  protected async ensureInitialized(): Promise<void> {
    if (!this.initialized) {
      await this.initialize()
    }
  }
}
